import { Anchor } from '@components/atoms/Anchor';
import Section from '@components/atoms/Section';
import Spacing from '@components/atoms/Spacing';
import BreadCrumbs from '@components/molecules/BreadCrumbs';
import PageContainer from '@components/molecules/PageContainer';
import RichTextSection from '@components/molecules/RichTextSection';
import { Note } from '@components/molecules/slices/SliceNote';
import { ButtonOnRichText } from '@components/molecules/SliceZone';
import DefaultTable from '@components/molecules/table/DefaultTable';
import DefaultHeader from '@components/organisms/headers/DefaultHeader';
import BREAKPOINTS from '@lib/constants/breakpoints';
import COLORS from '@lib/constants/colors';
import env from '@lib/constants/env';
import LINKS from '@lib/constants/links';
import ROUTES from '@lib/constants/routes';
import ibgeAPI from '@lib/data/ibgeAPI';
import { IPVA_STATE_FEATURES } from '@lib/data/ipva';
import airtableBases, { StateStats } from '@lib/helpers/airtableBases';
import { VehicleType, vehicleTypeToName } from '@lib/helpers/ipva';
import { STATES } from '@shared/constants';
import { percentageToString } from 'helpers';
import { GetStaticPaths, GetStaticProps } from 'next';
import { ArticleJsonLd, BreadcrumbJsonLd } from 'next-seo';
import Link from 'next/link';
import React from 'react';
import styled from 'styled-components';

const VehicleList = styled.ul`
  margin: 0 0;
  display: grid;
  grid-gap: 16px;
  grid-template-columns: 1fr 1fr 1fr;

  @media screen and (max-width: ${BREAKPOINTS.medium}) {
    grid-template-columns: 1fr;
  }
`;
interface Props {
  year: string;
  stats: StateStats;
  uf: keyof typeof STATES;
}

const Page = ({ year, uf, stats }: Props) => {
  if (!year) return <PageContainer loading />;

  const links = [
    {
      label: 'Luna',
      href: '/',
    },
    {
      label: 'IPVA',
      href: ROUTES.ipva,
    },
    {
      label: year,
      href: ROUTES.ipvaYear({ year }),
    },
    {
      label: STATES[uf],
      href: ROUTES.ipvaState({ year, uf }),
    },
  ];

  const stateName = STATES[uf];

  const feature = IPVA_STATE_FEATURES['2021'][uf];

  return (
    <PageContainer
      title={`IPVA ${year} ${uf} - Consulta, Valores, Pagamento`}
      metaDescription={`Confira tudo sobre o IPVA ${year} no estado de ${stateName}. Consultar valores, parcelamento, licenciamento e muito mais nesse guia.`}
      transparentNavbar
      transparentNavbarFontColor={COLORS.black}
      customAmpPage
    >
      <ArticleJsonLd
        url={`${env.baseUrl}/${ROUTES.ipvaState({ year, uf })}`}
        title={`IPVA ${year} ${uf} - Consulta, Valores, Pagamento`}
        description={`Confira tudo sobre o IPVA ${year} no estado de ${stateName}. Consultar valores, parcelamento, licenciamento e muito mais nesse guia.`}
        datePublished="2021-12-01T03:00:00.000Z"
        dateModified="2021-12-01T03:00:00.000Z"
        authorName={['Luna Seguros']}
        publisherName="Luna Seguros"
        publisherLogo={LINKS.logo}
        images={[LINKS.ogImage]}
      />
      <BreadcrumbJsonLd
        itemListElements={links.map((item, index) => ({
          position: index + 1,
          item: `${env.baseUrl}${item.href}`,
          name: item.label,
        }))}
      />
      <DefaultHeader
        title={`IPVA ${year} em ${stateName}`}
        subtitle={`Saiba tudo sobre o IPVA ${year} em ${stateName}: Valores, pagamento, datas  e muito mais nesse guia para o estado de ${stateName}`}
        pastel
      />
      {year === '2022' ? (
        <RichTextSection>
          <p>
            Você mora no estado da <strong>{stats.nome}</strong> e está querendo
            saber mais sobre o IPVA 2022 no seu estado?
          </p>
          <p>
            Com uma população de {stats.populacao.toLocaleString()} segundo o{' '}
            <a
              href={`https://www.ibge.gov.br/cidades-e-estados/${stats.uf}`}
              rel="noreferrer"
              target="_blank"
            >
              IBGE
            </a>{' '}
            e{' '}
            <strong>
              mais de {stats.veiculos.toLocaleString()} veículos rodando nas
              estradas do estado
            </strong>
            , o estado de {stats.nome} conta com uma área territorial de{' '}
            {stats.area.toLocaleString()}
            km².
          </p>
          <p>
            E com essa variedade de modelos no estado, saber tudo sobre IPVA
            2022 é bem imporante para não ter nehuma surpresa.
          </p>
          <strong>O que você vai no conteúdo:</strong>
          <ul>
            {[
              { href: 'ipva', text: 'O que é o IPVA 2022' },
              { href: 'calculo', text: `Como calcular o IPVA 2022 para ${uf}` },
              {
                href: 'vencimento',
                text: `Datas de Pagamento do IPVA 2022 ${uf}`,
              },
              {
                href: 'documento',
                text: `Licenciamento 2022 em ${uf}`,
              },
              { href: 'seguro', text: 'Seguro Auto' },
            ].map(({ href, text }) => (
              <li key={href}>
                <a
                  href={`#${href}`}
                  onClick={(e) => {
                    e.preventDefault();

                    const yOffset = -85;
                    const element = document.getElementById(
                      href,
                    ) as HTMLElement;
                    const y =
                      element?.getBoundingClientRect().top +
                      window.pageYOffset +
                      yOffset;

                    window.scrollTo({ top: y, behavior: 'smooth' });
                  }}
                >
                  {text}
                </a>
              </li>
            ))}
          </ul>
          <div id="ipva">
            <h2>{`IPVA ${year} ${stateName} - ${uf}`}</h2>
            <p>
              O IPVA, Imposto sobre Propriedade de Veículos Automotores, é um
              imposto obrigatório, que deve ser pago anualmente pelos
              proprietários de auto ou possuidores de veículos motorizados.
            </p>
            <ul>
              {Object.entries(vehicleTypeToName).map(([type, name]) => (
                <li key={type}>
                  <Link
                    href={ROUTES.ipvaType({
                      uf: uf as string,
                      year,
                      type: type as VehicleType,
                    })}
                    passHref
                  >
                    <Anchor>
                      IPVA
                      {` ${year} para ${name} em ${uf}`}
                    </Anchor>
                  </Link>
                </li>
              ))}
            </ul>
            <p>
              O IPVA {year} segue a{' '}
              <a
                href="https://legislacao.fazenda.sp.gov.br/Paginas/lei13296.aspx"
                rel="noreferrer"
                target="_blank"
              >
                <strong>Lei nº 13.296/2008</strong>
              </a>{' '}
              que estabelece o tratamento tributário do Imposto sobre a
              Propriedade de Veículos Automotores. O decreto que fixa o
              calendário para pagamento do IPVA relativamente ao exercício de{' '}
              {year} e o percentual de desconto para pagamento antecipado deve
              sair na primeira quinzena de dezembro de {Number(year) - 1}.
            </p>
            <p>
              No exercício de {Number(year) - 1}, o governo do estado de{' '}
              {stateName} ofereceu um desconto de 3% até os dias indicados, de
              acordo com o final da placa
            </p>
            <DefaultTable
              heads={['Final da Placa', 'Data']}
              rows={[
                [1, '07/01'],
                [2, '08/01'],
                [3, '11/01'],
                [4, '12/01'],
                [5, '13/01'],
                [6, '14/01'],
                [7, '15/01'],
                [8, '18/01'],
                [9, '19/01'],
                [0, '20/01'],
              ]}
            />
            <p>
              Lembrado que o desconto seria realizado apenas com pagamento
              integral.
            </p>
          </div>
          <div id="calculo">
            <h2>Cálculo do IPVA 2022 em {stateName}</h2>
            <p>
              Diferente do que a maioria das pessoas pensam, o ipva é calculado
              com o valor venal, e não de acordo com a tabela FIPE. No entanto,
              tanto o valor venal quanto o valor da tabela FIPE são afetados
              diretamente pelo preço de mercado.
            </p>
            <p>
              Você deve ter ficado feliz com o aumento do preço do seu carro ou
              moto, mas sinto muito em ser a pessoa que vai estragar um pouco
              essa felicidade: Praticamente todos os veículos tiveram aumento de
              preço durante o ano de 2021, sendo 20% a média deste aumento. Isto
              significa que, em média, você deverá pagar 20% a mais de IPVA em
              2022.
            </p>
            <h3>Como é calculado?</h3>
            <p>
              O cálculo do imposto é feito com base na aplicação da aliquota do
              estado de {stateName} em cima do valor venal do veículo, que é
              obtido através de diferentes fontes:
            </p>

            <ul>
              <li>
                <strong>Usado ou seminovo:</strong> O valor venal de 2022 é o
                preço médio de mercado vigente no mês de setembro de 2021. Esse
                valor é coletado pela Secretaria da Fazenda e devidamente
                publicado em tabela no Diário Oficial do Estado. Os valores das
                últimas tabelas disponíveis podem ser consultados{' '}
                <a
                  rel="noreferrer"
                  target="_blank"
                  href="https://www.imprensaoficial.com.br/suplementos/fazenda/IPVA2021/index.asp"
                >
                  aqui
                </a>
                . A tabela de valores venais completa pode ser baixada{' '}
                <a
                  rel="noreferrer"
                  target="_blank"
                  href="https://portal.fazenda.sp.gov.br/servicos/ipva/Downloads/IPVA%202021%20-%20Publica%C3%A7%C3%A3o%20DOE%20IMESP%20-%20Anexo%20I%20-%20Tabela%20de%20Valores%20Venais.pdf"
                >
                  aqui
                </a>
                . Obs: Ainda não foi divulgado o valor venal de 2021 para
                cálculo do IPVA de 2022.
              </li>
              <li>
                <strong>0 KM:</strong> O valor venal é o mesmo valor na nota
                fiscal.
              </li>
              <li>
                <strong>Novo arrematado em leilão:</strong> O valor venal é o
                valor da arrematação, acrescido das despesas cobradas ou
                debitadas do arrematante e dos valores dos tributos incidentes
                sobre a operação, ainda que não recolhidos.
              </li>
              <li>
                <strong>Importado do exeterior:</strong> O valor venal é o valor
                constante do documento de importação, acrescido dos valores dos
                tributos devidos em razão da importação, ainda que não
                recolhidos pelo importador.
              </li>
              <li>
                <strong>Incorporado do vendedor:</strong> O valor venal é o
                valor médio das operações com veículos do mesmo tipo que tenha
                comercializado no mês anterior ao da ocorrência do fato gerador.
              </li>
              <li>
                <strong>Incorporado do revendedor:</strong> O valor venal é o
                valor da operação de aquisição do veículo, constante do
                documento fiscal de aquisição.
              </li>
            </ul>
            <h3>Alíquotas do IPVA 2022 do estado de {stateName}</h3>
            <p>
              As alíquotas são determinadas de acordo com o tipo de veículo:
            </p>
            <DefaultTable
              heads={['Tipo de veículo', 'Alíquota']}
              rows={[
                [
                  <Link
                    key="link"
                    href={ROUTES.ipvaType({
                      uf: uf as string,
                      year,
                      type: 'caminhoes',
                    })}
                    passHref
                  >
                    <Anchor>Caminhões</Anchor>
                  </Link>,
                  percentageToString(feature?.caminhoes.aliquota || 0.015),
                ],
                [
                  <Link
                    key="link"
                    href={ROUTES.ipvaType({
                      uf: uf as string,
                      year,
                      type: 'onibus',
                    })}
                    passHref
                  >
                    <Anchor>Ônibus e micro-ônibus</Anchor>
                  </Link>,

                  percentageToString(feature?.onibus.aliquota || 0.015),
                ],
                [
                  <Link
                    key="link"
                    href={ROUTES.ipvaType({
                      uf: uf as string,
                      year,
                      type: 'camionetas',
                    })}
                    passHref
                  >
                    <Anchor>
                      Caminhonetes cabine simples (capacidade até 3 passageiros)
                    </Anchor>
                  </Link>,
                  percentageToString(feature?.camionetas.aliquota || 0.015),
                ],
                [
                  <Link
                    key="link"
                    href={ROUTES.ipvaType({
                      uf: uf as string,
                      year,
                      type: 'motos',
                    })}
                    passHref
                  >
                    <Anchor>
                      Motocicletas, ciclomotores, motonetas, triciclos e
                      quadriciclos
                    </Anchor>
                  </Link>,
                  percentageToString(feature?.motos.aliquota || 0.015),
                ],
                [
                  <Link
                    key="link"
                    href={ROUTES.ipvaType({
                      uf: uf as string,
                      year,
                      type: 'automoveis',
                    })}
                    passHref
                  >
                    <Anchor>
                      Demais veículos, inclusive automóveis de passeio tipo flex
                    </Anchor>
                  </Link>,
                  percentageToString(feature?.automoveis.aliquota || 0.015),
                ],
              ]}
            />
            <p>No exercício de 2022:</p>
            <ul>
              <li>
                Será aplicada a alíquota de 3% para veículos usados que
                utilizarem motor especificado para funcionar, exclusivamente,
                com os seguintes combustíveis: álcool, gás natural veicular ou
                eletricidade, ainda que combinados entre si.
              </li>
              <li>
                A mesma alíquota de 3% será também aplicada aos veículos novos
                que atendam à condição do item anterior e cuja data de nota
                fiscal seja inferior a 15/01/2022.
              </li>
              <li>
                Será aplicada a alíquota de 3% (três por cento) para veículos
                fabricados até 31 de dezembro de 2008 que utilizarem motor
                especificado para funcionar exclusivamente a gasolina, quando
                adaptado para funcionar de maneira combinada com gás natural
                veicular ou gás natural comprimido.
              </li>
            </ul>

            <h3>Exemplo de cálculo do IPVA 2022 em {stateName}:</h3>
            <Note>
              <p>
                <strong>Modelo do veículo</strong>: Mercedes-Benz A45 AMG 2021
              </p>
              <p>
                <strong>Valor Venal:</strong> R$416.900
              </p>
              <p>
                <strong>Alíquota:</strong> 4,0%{' '}
              </p>
              <p>
                <strong>Valor do IPVA 2022:</strong> R$416.900 * 4% = 3 de
                R$5,559
              </p>
            </Note>
          </div>

          <div id="vencimento">
            <h2>Vencimento do IPVA 2022 em {uf}</h2>
            <p>
              Como já dito anteriormente, há a possibilidade de receber 3% de
              desconto para pagemento único antecipado, ou então efetuar o
              pagamento em até 3 parcelas iguais ao longos dos 3 primeiros meses
              do ano de 2022.
            </p>
            <h3>Veículos Usados</h3>
            <p>
              Automóveis, Camionetas, Caminhonetes, Ônibus, Micro-ônibus, Motos
              e similares
            </p>
            <DefaultTable
              heads={[
                'Final da Placa',
                '1ª Parcela ou Cota Única Com Desconto (*) Janeiro',
                '2ª Parcela ou Cota Única Sem Desconto Fevereiro',
                '3ª Parcela Março',
              ]}
              rows={[
                ['Final 1', '07/01', '09/02', '09/03'],
                ['Final 2', '08/01', '10/02', '10/03'],
                ['Final 3', '11/01', '11/02', '11/03'],
                ['Final 4', '12/01', '12/02', '12/03'],
                ['Final 5', '13/01', '18/02', '15/03'],
                ['Final 6', '14/01', '19/02', '16/03'],
                ['Final 7', '15/01', '22/02', '17/03'],
                ['Final 8', '18/01', '23/02', '18/03'],
                ['Final 9', '19/01', '24/02', '19/03'],
                ['Final 0', '20/01', '25/02', '22/03'],
              ]}
            />
            <p>
              *O pagamento do IPVA em cota única terá desconto de 3,0 % (três
              por cento).
            </p>
            <h3>Veículos Novos</h3>
            <p>
              Pagamento do IPVA em cota única com desconto de 3,0 % até o 5º dia
              útil posterior à data da emissão do documento fiscal.
            </p>
            <p>
              Pagamento do IPVA em cota única sem desconto até 30 dias contados
              da data da emissão do documento fiscal.
            </p>
            <p>Pagamento Parcelado do IPVA:</p>
            <ul>
              <li>
                1ª parcela: até 30 dias da data de emissão do documento fiscal.
              </li>
              <li>
                2ª parcela: no mesmo dia do mês subsequente ao do vencimento da
                1ª parcela.
              </li>
              <li>
                3ª parcela: no mesmo dia do segundo mês subsequente ao do
                vencimento da 1ª parcela.
              </li>
            </ul>
          </div>
          {/* <div>
            <h2>Pagamento do IPVA 2022</h2>
            <p>
              Para efetuar o pagamento do IPVA de 2022, basta [[FLUXO DE
              PAGAMENTO DO IPVA LUNA + ZAPAY]].
            </p>
            <p>
              Aqui você consegue efetuar o pagamento em até 12x no cartão de
              crédito ou até 3x no boleto.
            </p>
          </div> */}
          <div id="documento">
            <h2>Documentação em dia</h2>

            <h3>Débitos e multas</h3>

            <p>
              Caso queira começar o ano com a documentação do seu veículo em
              dia, o primeiro passo é quitar todos os débitos e multas que
              houver em seu veículo.
            </p>
            <h3>Licenciamento 2022</h3>
            <p>
              O licenciamento, também conhecido como CLRV, é feito anualmente
              pagamento uma taxa anual, referente a atualização do documento. O
              CRLV era enviado pelo detran em papel, mas agora pode ser emitido
              digitalmente direto pelo seu celular.
            </p>
            <p>
              O valor do licenciamento de 2022 gira em torno dos R$100,00 e a
              data de pagamento varia de acordo com o final da placa. Lembrando
              que não há como licenciar o veículo, sem antes efetuar o pagamento
              do IPVA 2022.
            </p>
            <DefaultTable
              heads={['Final da Placa', 'Data']}
              rows={[
                [1, '09/03/2022'],
                [2, '10/03/2022'],
                [3, '13/03/2022'],
                [4, '14/03/2022'],
                [5, '15/03/2022'],
                [6, '16/03/2022'],
                [7, '17/03/2022'],
                [8, '20/03/2022'],
                [9, '21/03/2022'],
                [0, '22/03/2022'],
              ]}
            />
          </div>
          <div id="seguro">
            <h2>Seguro Auto</h2>
            <p>
              Começo de ano, normalmente é a data perfeita para encontrar o
              melhor seguro pelo melhor preço, não é mesmo? A Luna é uma
              plataforma que te ajuda a cotar, comparar e contratar um seguro
              para seu veículo (carro ou moto) nas maiores seguradoras do Brasil
              em menos de 90 segundos.
            </p>
            <p>
              Somos uma corretora digital, regulada pela SUSEP, que usa
              tecnologia e design para ajudar as pessoas a encontrarem o melhor
              seguro pelo melhor preço. Quer encontrar o seguro ideal para seu
              auto? É só cotar grátis aqui.
            </p>
            <ButtonOnRichText text="Cotar Seguro Auto" />
          </div>
          <div>
            <h2>
              Sobre {stats.nome} - {stats.uf.toUpperCase()}
            </h2>
            <p>
              {stats.nome} tem como capital {stats.capital} e quem nasce em{' '}
              {stats.nome} é chamado de {stats.gentilico}. Seu governador atual
              é {stats.governador.toLocaleUpperCase()}
            </p>
          </div>
        </RichTextSection>
      ) : (
        <RichTextSection>
          <Spacing bottom={4}>
            {year !== '2023' && (
              <p>
                <Link href={ROUTES.ipvaState({ year: '2023', uf })} passHref>
                  <a>Veja tudo sobre o IPVA 2023 {stateName}</a>
                </Link>
              </p>
            )}
            <p>
              Escolha qual o tipo de veículo que pretende consultar no{' '}
              {stateName} para ver as marcas e fazer a simulação comparativa do
              valor IPVA a pagar em
              {` ${year}`}
            </p>
          </Spacing>
          <VehicleList>
            {Object.entries(vehicleTypeToName).map(([type, name]) => (
              <li key={type}>
                <Link
                  href={ROUTES.ipvaType({
                    uf: uf as string,
                    year,
                    type: type as VehicleType,
                  })}
                  passHref
                >
                  <Anchor>
                    IPVA
                    {` ${year} para ${name} `}
                  </Anchor>
                </Link>
              </li>
            ))}
          </VehicleList>
        </RichTextSection>
      )}

      <Section>
        <BreadCrumbs links={links} />
      </Section>
    </PageContainer>
  );
};

export const getStaticProps: GetStaticProps<Props> = async ({ params }) => {
  const { year } = params as Record<string, string>;

  const uf = params?.uf as keyof typeof STATES;

  const state = STATES[uf];

  if (!state) return { notFound: true };

  const [stats] = await Promise.all([
    airtableBases.getStatsByUf(uf),
    ibgeAPI.districts(uf),
  ]);

  if (!stats) return { notFound: true };

  return {
    props: {
      year,
      uf,
      stats,
    },
  };
};

export const getStaticPaths: GetStaticPaths = async () => {
  const paths = Object.keys(STATES)
    .map((uf) => [
      ROUTES.ipvaState({ uf, year: '2022' }),
      ROUTES.ipvaState({ uf, year: '2023' }),
    ])
    .flat();

  return {
    paths,
    fallback: true,
  };
};

export default Page;
